/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Unified Estimate Details Hook
 *
 * Replaces: use-estimate-details-optimized
 *
 * Following React Hooks best practices:
 * - React Query for caching and state management
 * - Flexible options for related data
 * - Proper TypeScript typing
 */

"use client";

import { useAuthSafe } from "@/hooks/auth/use-auth-safe";
import { useOrganizationQueryGuard } from "@/hooks/use-organization-query";
import { createApiClient, isApiFailure } from "@/lib/api/api-client";
import { toastFromApiFailure } from "@/lib/api/toast-utils";
import {
  QUERY_GC_TIME,
  QUERY_RETRY,
  QUERY_STALE_TIME,
  getRetryDelay,
} from "@/lib/constants/query-config";
import { estimateQueryKeys } from "@/lib/query/query-keys";
import type { Database } from "@/lib/supabase/types";
import { logger } from "@/lib/utils/logger";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { EstimateDetails } from "@/app/app/estimates/estimates-actions-types";

type EstimateRow = Database["public"]["Tables"]["estimates"]["Row"];

// Stable fallback references — using `?? []` / `?? {...}` inline in the return
// creates a new reference every render, which cascades through consumer
// useMemo/useEffect deps and causes infinite render loops (see CCP-TBD).
const EMPTY_LINE_ITEMS: EstimateDetails["lineItems"] = [];
const DEFAULT_COUNT = { lineItems: 0, documents: 0 } as const;

interface UseEstimateDetailsOptions {
  estimateId: string | null;
  includeLineItems?: boolean;
  includeProject?: boolean;
  includeClient?: boolean;
  includeAnalysis?: boolean;
  includeComparisons?: boolean;
  enabled?: boolean;
}

export function useEstimateDetails({
  estimateId,
  includeLineItems = true,
  includeProject = true,
  includeClient = true,
  includeAnalysis = false,
  includeComparisons = false,
  enabled = true,
}: UseEstimateDetailsOptions) {
  const { getToken, isLoaded, isSignedIn } = useAuthSafe();
  const { organizationId, queryEnabled: organizationReady } =
    useOrganizationQueryGuard({
      enabled,
      debugKey: "useEstimateDetails",
    });
  const queryClient = useQueryClient();

  // Use centralized query key factory (convert null to undefined for type safety)
  const queryKey = estimateQueryKeys.detail(
    organizationId ?? undefined,
    estimateId ?? undefined,
    {
      includeLineItems,
      includeProject,
      includeClient,
      includeAnalysis,
      includeComparisons,
    },
  );

  // Use centralized staleTime/gcTime constants (estimate-details are CRITICAL like estimates)
  const isQueryEnabled =
    isLoaded && isSignedIn && organizationReady && Boolean(estimateId);

  const queryResult = useQuery({
    queryKey,
    enabled: isQueryEnabled,
    queryFn: async (): Promise<EstimateDetails> => {
      if (!estimateId) throw new Error("Estimate ID is required");

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      });

      // OPTIMIZATION: Build query params only with requested includes
      // This reduces URL length and makes caching more effective
      const params = new URLSearchParams();
      if (includeLineItems) params.set("includeLineItems", "true");
      if (includeProject) params.set("includeProject", "true");
      if (includeClient) params.set("includeClient", "true");
      if (includeAnalysis) params.set("includeAnalysis", "true");
      if (includeComparisons) params.set("includeComparisons", "true");

      // OPTIMIZATION: React Query automatically deduplicates requests with the same queryKey
      // Multiple components requesting the same data will share a single request
      const res = await api.get<EstimateDetails>(
        `/api/estimates/${estimateId}?${params.toString()}`,
      );

      if (isApiFailure(res)) {
        throw new Error(res.message);
      }

      if (!res.data) {
        throw new Error("Estimate details response is empty");
      }

      return res.data;
    },
    staleTime: QUERY_STALE_TIME.CRITICAL, // estimate-details are CRITICAL
    gcTime: QUERY_GC_TIME.CRITICAL,
    retry: QUERY_RETRY.DEFAULT, // Matches QueryProvider default
    retryDelay: getRetryDelay,
    // OPTIMIZATION: Keep previous data visible while refetching to prevent loading flicker
    placeholderData: keepPreviousData,
    // OPTIMIZATION: Prevent unnecessary refetches when data is fresh
    refetchOnWindowFocus: false, // Don't refetch on window focus (data is critical but changes infrequently)
    refetchOnReconnect: true, // Refetch when network reconnects (important for offline scenarios)
  });

  const { data, isLoading, error, isError, refetch, isFetching } = queryResult;

  const invalidate = () => {
    if (!estimateId) return;
    queryClient.invalidateQueries({
      queryKey,
      refetchType: "active", // Only refetch active queries
    });
  };

  const updateEstimateMutation = useMutation({
    mutationFn: async (updates: Partial<EstimateRow>) => {
      if (!estimateId) {
        throw new Error("Estimate ID is required to update estimate");
      }

      const api = createApiClient({
        getToken,
        getOrganizationId: () => organizationId ?? undefined,
        autoRetry429: true,
      });

      const updatePayload: Record<string, unknown> = {};
      if (updates.title !== undefined) updatePayload.title = updates.title;
      if (updates.description !== undefined)
        updatePayload.description = updates.description;
      if (updates.budget_id !== undefined)
        updatePayload.budget_id = updates.budget_id;
      if (updates.project_id !== undefined)
        updatePayload.project_id = updates.project_id;
      if (updates.client_id !== undefined)
        updatePayload.client_id = updates.client_id;
      if (updates.status !== undefined) updatePayload.status = updates.status;
      if (updates.subtotal !== undefined)
        updatePayload.subtotal = updates.subtotal;
      if (updates.tax_amount !== undefined)
        updatePayload.tax_amount = updates.tax_amount;
      if (updates.total_amount !== undefined)
        updatePayload.total_amount = updates.total_amount;
      if (updates.version !== undefined)
        updatePayload.version = updates.version;
      if (updates.assigned_to !== undefined)
        updatePayload.assigned_to = updates.assigned_to;
      if (updates.owner_id !== undefined)
        updatePayload.owner_id = updates.owner_id;
      if (updates.division_id !== undefined)
        updatePayload.division_id = updates.division_id;
      if (updates.contact_id !== undefined)
        updatePayload.contact_id = updates.contact_id;
      if (updates.jobsite_id !== undefined)
        updatePayload.jobsite_id = updates.jobsite_id;
      if (updates.due_date !== undefined)
        updatePayload.due_date = updates.due_date;

      const res = await api.put<EstimateRow>(
        `/api/estimates/${estimateId}`,
        updatePayload,
      );

      if (isApiFailure(res)) {
        toastFromApiFailure("Failed to update estimate", res);
        throw new Error(res.message);
      }

      const updatedEstimate = res.data;
      if (!updatedEstimate) {
        throw new Error("No data returned from update");
      }

      return updatedEstimate;
    },
    onMutate: async (updates) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<EstimateDetails>(queryKey);

      // Optimistically update the cache with the new values
      queryClient.setQueryData<EstimateDetails>(queryKey, (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          estimate: {
            ...oldData.estimate,
            ...updates,
            updated_at: new Date().toISOString(),
          },
        };
      });

      return { previous };
    },
    onError: (error, _updates, context) => {
      // Rollback to previous state on error
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
      logger.error("Failed to update estimate", { error: error.message });
    },
    onSuccess: (updatedEstimate, updates) => {
      // Replace optimistic data with server truth
      queryClient.setQueryData<EstimateDetails>(queryKey, (oldData) => {
        if (!oldData) return oldData;
        return { ...oldData, estimate: updatedEstimate };
      });

      if (updates.budget_id !== undefined) {
        queryClient.invalidateQueries({
          queryKey: ["estimate-budget-rates"],
          exact: false,
        });
      }
    },
    onSettled: () => {
      // Always refetch to ensure cache matches server
      queryClient.invalidateQueries({ queryKey, refetchType: "active" });
      queryClient.invalidateQueries({
        queryKey: estimateQueryKeys.lists(),
        refetchType: "active",
      });
    },
  });

  const updateEstimate = updateEstimateMutation.mutateAsync;

  return {
    estimate: data?.estimate ?? null,
    lineItems: data?.lineItems ?? EMPTY_LINE_ITEMS,
    project: data?.project ?? null,
    client: data?.client ?? null,
    jobsite: data?.jobsite ?? null,
    budgetRates: data?.budgetRates ?? null,
    analysis: data?.analysis ?? null,
    comparisons: data?.comparisons ?? null,
    economics: data?.economics ?? null,
    breakdown: data?.breakdown ?? null,
    count: data?._count ?? DEFAULT_COUNT,
    isLoading,
    isFetching,
    error: isError && error instanceof Error ? error.message : null,
    lastUpdated: queryResult.dataUpdatedAt
      ? new Date(queryResult.dataUpdatedAt)
      : null,
    isStale: queryResult.isStale,
    refetch,
    refresh: refetch, // Alias for compatibility
    invalidate,
    updateEstimate,
    isUpdating: updateEstimateMutation.isPending,
    clearError: () => {
      // React Query handles error clearing via refetch
    },
  };
}
